import { CartLinesUpdateEvent } from '@shopify/events';

const cache = new Map();

/**
 * @param {string} src
 */
function sizedImage(src) {
  if (!src) return '';
  const absolute = src.startsWith('//') ? `https:${src}` : src;
  const url = new URL(absolute, window.location.origin);
  url.searchParams.set('width', '240');
  return url.toString();
}

/**
 * @param {number} cents
 */
function formatMoney(cents) {
  const amount = (Number(cents) / 100).toFixed(2);
  return `$${amount}`;
}

/**
 * @param {string} seeds
 * @param {number} maxPrice
 */
async function loadPicks(seeds, maxPrice) {
  const key = `${seeds}|${maxPrice}`;
  if (cache.has(key)) return cache.get(key);

  const inCart = new Set(seeds.split(',').filter(Boolean));
  const seen = new Set();
  /** @type {Array<{ id: number, variantId: number, title: string, price: number, image: string }>} */
  const picks = [];

  for (const seed of inCart) {
    if (picks.length >= 3) break;
    const response = await fetch(
      `/recommendations/products.json?product_id=${encodeURIComponent(seed)}&limit=10&intent=related`
    );
    if (!response.ok) continue;
    const data = await response.json();
    for (const product of data.products ?? []) {
      if (picks.length >= 3) break;
      const id = String(product.id);
      if (inCart.has(id) || seen.has(id) || product.available === false || product.requires_selling_plan) continue;
      const price = Number(product.price);
      if (!Number.isFinite(price) || price <= 0 || price >= maxPrice) continue;
      const variant = (product.variants ?? []).find((item) => item.available && Number(item.price) < maxPrice);
      if (!variant) continue;
      seen.add(id);
      picks.push({
        id: product.id,
        variantId: variant.id,
        title: product.title,
        price,
        image: sizedImage(product.featured_image),
      });
    }
  }

  cache.set(key, picks);
  return picks;
}

class EcjCartStack extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  connectedCallback() {
    this.#observe();
    this.#load();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #observe() {
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      const row = this.querySelector('[data-row]');
      if (row && row.childElementCount === 0) this.#load();
    });
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  async #load() {
    const row = this.querySelector('[data-row]');
    const status = this.querySelector('[data-status]');
    if (!(row instanceof HTMLElement) || !(status instanceof HTMLElement)) return;
    if (row.childElementCount > 0 || this.dataset.loading === '1') return;

    this.dataset.loading = '1';
    let picks = [];
    try {
      picks = await loadPicks(this.dataset.seeds ?? '', Number(this.dataset.maxPrice || 6000));
      const liveRow = this.querySelector('[data-row]');
      const liveStatus = this.querySelector('[data-status]');
      if (!(liveRow instanceof HTMLElement) || !(liveStatus instanceof HTMLElement) || !this.isConnected) return;
      if (!picks.length) {
        this.hidden = true;
        return;
      }
      liveRow.replaceChildren(...picks.map((product) => this.#card(product, liveStatus)));
      this.hidden = false;
      this.dataset.tries = '0';
    } catch (error) {
      console.warn('[ecj-cart-offer]', error);
      this.hidden = true;
    } finally {
      this.dataset.loading = '0';
      const liveRow = this.querySelector('[data-row]');
      const tries = Number(this.dataset.tries || 0);
      if (picks.length && liveRow && liveRow.childElementCount === 0 && this.isConnected && tries < 4) {
        this.dataset.tries = String(tries + 1);
        this.#load();
      }
    }
  }

  /**
   * @param {{ id: number, variantId: number, title: string, price: number, image: string }} product
   * @param {HTMLElement} status
   */
  #card(product, status) {
    const card = document.createElement('article');
    card.className = 'ecj-stack__card';

    const media = document.createElement('div');
    media.className = 'ecj-stack__media';
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.title;
      image.width = 120;
      image.height = 120;
      image.loading = 'lazy';
      media.append(image);
    }

    const price = document.createElement('p');
    price.className = 'ecj-stack__price';
    price.textContent = formatMoney(product.price);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ecj-stack__add';
    button.setAttribute('aria-label', `Add ${product.title} to cart`);
    button.append(plusIcon(), document.createTextNode('Add'));
    button.addEventListener('click', () => this.#add(product, button, status));

    card.append(media, price, button);
    return card;
  }

  /**
   * @param {{ id: number, variantId: number, title: string }} product
   * @param {HTMLButtonElement} button
   * @param {HTMLElement} status
   */
  async #add(product, button, status) {
    const buttons = this.querySelectorAll('button');
    buttons.forEach((item) => {
      item.disabled = true;
    });
    status.textContent = '';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(product.variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const routes = globalThis.Theme?.routes ?? { cart_add_url: '/cart/add.js', cart_url: '/cart' };
      const addResponse = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: product.variantId, quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await addResponse.json();
      if (added.status) {
        throw new Error(added.description || added.message || 'Could not add this bracelet');
      }

      const cartResponse = await fetch(`${routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();
      cache.clear();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ecj-cart-offer',
          sourceId: 'ecj-cart-offer',
          itemCount: 1,
          productId: String(product.id),
          sections: added.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
      buttons.forEach((item) => {
        item.disabled = false;
      });
      status.textContent = error instanceof Error ? error.message : 'Could not add this bracelet';
    }
  }
}

function plusIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 12 12');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M6 1.5v9M1.5 6h9');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.4');
  path.setAttribute('stroke-linecap', 'round');
  svg.append(path);
  return svg;
}

if (!customElements.get('ecj-cart-stack')) {
  customElements.define('ecj-cart-stack', EcjCartStack);
}
